import type { ReactNode } from 'react';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import type { SelectOption } from '@/shared/ui/select';
import { Textarea } from '@/shared/ui/textarea';
import type {
  AttributeDraft,
  AttributeDraftPatch,
} from '@/modules/catalog/pages/setup-page/types';
import { OutcomeLine } from '@/modules/catalog/pages/setup-page/parts/outcome-line';
import { DraftAccept } from '@/modules/catalog/pages/setup-page/parts/draft-accept';

/**
 * ONE proposed attribute, editable.
 *
 * The key, the name, the kind, the options and the axis are all editable
 * controls. The proposed DESCRIPTION is shown as it stands: the dictionary's
 * create contract has no description member, so the console displays it and
 * does not send it — a body carrying it would be refused.
 */
export type AttributeRowProps = {
  readonly draft: AttributeDraft;
  readonly kindOptions: SelectOption[];
  readonly axisOptions: SelectOption[];
  readonly onChange: (id: string, patch: AttributeDraftPatch) => void;
};

export function AttributeRow({
  draft,
  kindOptions,
  axisOptions,
  onChange,
}: AttributeRowProps): ReactNode {
  return (
    <article className="space-y-3 rounded-lg border border-border bg-surface-raised p-3">
      <DraftAccept
        label={`Accept ${draft.name}`}
        accepted={draft.accepted}
        uncertain={draft.uncertain}
        onToggle={() => onChange(draft.id, { accepted: !draft.accepted })}
      />

      <AttributeFields
        draft={draft}
        kindOptions={kindOptions}
        axisOptions={axisOptions}
        onChange={onChange}
      />

      {draft.description === '' ? null : (
        <Textarea label="Proposed description" value={draft.description} readOnly />
      )}

      {draft.assignToCategoryIds.length === 0 ? null : (
        <p className="text-sm text-content-muted">
          The proposal would attach this to {draft.assignToCategoryIds.length} categor
          {draft.assignToCategoryIds.length === 1 ? 'y' : 'ies'}.
        </p>
      )}

      <OutcomeLine outcome={draft.outcome} />
    </article>
  );
}

/** The editable fields of one proposed attribute. */
function AttributeFields({
  draft,
  kindOptions,
  axisOptions,
  onChange,
}: AttributeRowProps): ReactNode {
  return (
    <>
      <div className="grid gap-3 md:grid-cols-2">
        <Input
          label="Key"
          hint="Lower snake case. The dictionary refuses anything else."
          value={draft.key}
          onChange={(event) => onChange(draft.id, { key: event.target.value })}
        />
        <Input
          label="Name"
          value={draft.name}
          onChange={(event) => onChange(draft.id, { name: event.target.value })}
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Select
          label="Kind"
          options={kindOptions}
          value={draft.kind}
          onChange={(event) =>
            onChange(draft.id, { kind: event.target.value as AttributeDraft['kind'] })
          }
        />
        <Select
          label="Variant axis"
          hint="Only size, colour and material create more than one SKU."
          options={axisOptions}
          value={draft.variantAxis}
          onChange={(event) =>
            onChange(draft.id, { variantAxis: event.target.value as AttributeDraft['variantAxis'] })
          }
        />
      </div>

      <Input
        label="Options"
        hint="Comma separated. Required for a select kind, and refused for the others."
        value={draft.optionsText}
        onChange={(event) => onChange(draft.id, { optionsText: event.target.value })}
      />
    </>
  );
}
